"use client";

import type { FC } from "react";
import { useEffect } from "react";
import { motion } from "motion/react";
import { cn } from "utils";
import { FitnessScoreLegendCard } from "../fitness/FitnessScoreLegendCard";
import { ONBOARDING_WIZARD_ASSETS } from "../onboardingWizardAssets";
import { SCORE_INTRO_COPY } from "../onboardingWizardCopy";
import { OnboardingResponsiveCtaFooter } from "../shared/OnboardingResponsiveCtaFooter";
import { ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS } from "../shared/onboardingFloatingCtaTokens";
import { trackEvent } from "@/src/utils/tracking";
import { AppImage } from "@/src/components/NextImage";

interface ScoreIntroStepProps {
  onContinue: () => void;
}

const AXIS_LABELS = [
  {
    key: "control",
    label: SCORE_INTRO_COPY.axisControl,
    className: "left-1/2 top-0 -translate-x-1/2",
  },
  {
    key: "growth",
    label: SCORE_INTRO_COPY.axisGrowth,
    className: "right-0 bottom-0",
  },
  {
    key: "safety",
    label: SCORE_INTRO_COPY.axisSafety,
    className: "left-0 bottom-0 text-right",
  },
] as const;

export const ScoreIntroStep: FC<ScoreIntroStepProps> = ({ onContinue }) => {
  useEffect(() => {
    trackEvent("ff_intro_view");
  }, []);

  const handleContinue = () => {
    trackEvent("ff_intro_getscore_tap");
    onContinue();
  };

  return (
    <div
      className="relative flex min-h-dvh w-full max-md:h-dvh flex-col overflow-x-hidden bg-background_solid_neutral_secondary max-md:overflow-y-hidden"
      data-testid="onboarding-score-intro-step"
      data-figma-node="97267:70506"
      data-figma-node-pc="97283:76626"
    >
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden
      >
        <picture className="block size-full">
          <source
            media="(min-width: 960px)"
            srcSet={ONBOARDING_WIZARD_ASSETS.scoreIntroBgDesktop}
          />
          <AppImage
            src={ONBOARDING_WIZARD_ASSETS.scoreIntroBgMobile}
            alt=""
            className="size-full max-w-none object-cover object-center"
            data-testid="score-intro-background"
          />
        </picture>
      </div>

      <div
        className={cn(
          "relative z-[1] flex min-h-0 flex-1 flex-col items-center gap-6 overflow-x-hidden overflow-y-auto no-scrollbar px-8 pt-[60px] md:mx-auto md:w-full md:max-w-[460px] md:px-8 md:pb-10",
          ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS,
        )}
      >
        <motion.div
          className="relative h-[262px] w-[304px] shrink-0"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          data-testid="score-intro-radar-group"
        >
          <AppImage
            src={ONBOARDING_WIZARD_ASSETS.scoreIntroRadarDecor}
            alt=""
            width={224}
            height={228}
            className="absolute left-1/2 top-[calc(50%+16.89px)] h-[228px] w-[224px] -translate-x-1/2 -translate-y-1/2 object-cover"
            data-testid="score-intro-radar-decor"
          />
          {AXIS_LABELS.map((axis) => (
            <p
              key={axis.key}
              className={cn(
                "absolute body_bold_s text-content_accent4_primary",
                axis.className,
              )}
            >
              {axis.label}
            </p>
          ))}
        </motion.div>

        <motion.div
          className="flex w-full flex-col gap-8"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, delay: 0.08, ease: "easeOut" }}
        >
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="headline_regular_m text-content_primary tracking-[-0.96px]">
              <span className="block">{SCORE_INTRO_COPY.titleLine1}</span>
              <span className="block">{SCORE_INTRO_COPY.titleLine2}</span>
            </h1>
            <p className="body_m text-content_secondary">
              {SCORE_INTRO_COPY.subtitle}
            </p>
          </div>
          <FitnessScoreLegendCard />
        </motion.div>

        <div className="hidden w-full md:block md:pt-2">
          <button
            type="button"
            data-testid="score-intro-cta-desktop"
            className={cn(
              "button lg_button primary_button button_l h-12 w-full rounded-lg text-content_inverse_primary",
            )}
            onClick={handleContinue}
          >
            {SCORE_INTRO_COPY.cta}
          </button>
        </div>
      </div>

      <OnboardingResponsiveCtaFooter
        inset="narrow"
        testId="score-intro-footer"
        className="md:hidden"
      >
        <button
          type="button"
          data-testid="score-intro-cta"
          className={cn(
            "button lg_button primary_button button_l h-12 w-full rounded-lg text-content_inverse_primary",
          )}
          onClick={handleContinue}
        >
          {SCORE_INTRO_COPY.cta}
        </button>
      </OnboardingResponsiveCtaFooter>
    </div>
  );
};
